import * as React from 'react';
import { isEqual, pickBy, size } from 'lodash-es';

import { Search } from '@weddingspot/ws-api-client';
import { useWiderThan, FeatureFlagContext, FLAG_SHOPPING_CART_FOR_SRP } from '@weddingspot/ws-core-components';
const { SearchIcon } = require('@cvent/carina-icon');

import { FilterBar as FBar } from '../filter-bar';
import BadgeButton, { BadgeButtonProps } from './BadgeButton';
import { SRPFormikValues } from './index';
import { useFormikContext } from 'formik';
import { FilterContext } from '../filter-bar/FilterContext';
import { MAX as MAX_BUDGET } from './BudgetRangeFilter';
import { MAX as MAX_GC } from './GuestCountFilter';
import MwebFitlers from './MwebFilters';
import cn from 'classnames';

const _styles = require('./FilterBar.scss');

interface Props {
    filtersChoices: Search.SearchFilters | null | undefined;
    selectedMarket?: any;
}

const filterTypes = ['Locations', 'Venue Style', 'Outdoor', 'More Filters', 'Budget', 'Guests', 'Services', 'Alcohol & Beverages'];

export type FilterTypes = typeof filterTypes[number];

export const FilterBar = (props: Props) => {
    const { filtersChoices, selectedMarket } = props;
    const { flagEnabled } = React.useContext(FeatureFlagContext);
    const showShoppingCart = flagEnabled(FLAG_SHOPPING_CART_FOR_SRP);

    if (!filtersChoices) {
        return null;
    }

    const { locations, styles, budget, guests, services, byo } = filtersChoices;
    const { values: formikValues } = useFormikContext<SRPFormikValues>();

    const outdoorStyleValue = styles?.options.find((e) => e.label === 'Outdoor')!.value;

    const isSmallScreen = !useWiderThan(750);

    const budgetCount = isEqual(formikValues.budget, [0, null]) || isEqual(formikValues.budget, [0, MAX_BUDGET]) ? 0 : 1;

    const guestCount = isEqual(formikValues.guestCount, [0, null]) || isEqual(formikValues.guestCount, [0, MAX_GC]) ? 0 : 1;

    const srCount = formikValues.sr !== 1 ? 1 : 0;
    const byoCount = formikValues.byo !== false ? 1 : 0;

    const moreFiltersTouched = budgetCount + guestCount + srCount + byoCount;

    const renderBadge = (key: string, badgeCount: number, badgeProps?: Partial<BadgeButtonProps>) => {
        const { selectedFilter } = React.useContext(FilterContext);

        return <BadgeButton label={key} isActive={selectedFilter === key} badgeCount={badgeCount} {...badgeProps} />;
    };

    const renderFilters = (selectedFilter: string | null, setSelectedFilter: (query: string | null) => void) => {
        return (
            filtersChoices &&
            selectedMarket && (
                <MwebFitlers
                    isSmallScreen={isSmallScreen}
                    filtersChoices={filtersChoices}
                    selectedMarket={selectedMarket}
                    visibleFilters={selectedFilter}
                    onDismiss={() => {
                        setSelectedFilter(null);
                    }}
                />
            )
        );
    };

    const renderSearchFilter = (selectedFilter: string | null, setSelectedFilter: (query: string | null) => void) => {
        return (
            isSmallScreen && (
                <div
                    className={cn(_styles.mwebLocationSearchInputWrapper, {
                        [_styles.searchIconWithCart]: showShoppingCart,
                    })}
                    onClick={() => {
                        setSelectedFilter('Search');
                    }}
                >
                    <div className={_styles.searchIcon}>
                        <SearchIcon color='#69717A' size='l' />
                    </div>
                </div>
            )
        );
    };
    return (
        <FBar
            availableFilters={filterTypes}
            renderHeaderElement={(key) => {
                switch (key) {
                    case 'Locations':
                        return locations && renderBadge(key, size(pickBy(formikValues.region)));
                    case 'Venue Style':
                        return styles && renderBadge(key, size(pickBy(formikValues.styles)));
                    case 'Outdoor':
                        return (
                            isSmallScreen &&
                            outdoorStyleValue &&
                            renderBadge(key, (formikValues.styles && formikValues.styles[outdoorStyleValue]) === true ? 1 : 0, {
                                badgeCountActiveOnly: true,
                                isActive: false,
                            })
                        );
                    case 'More Filters':
                        return isSmallScreen && (budget || guests || services || byo) && renderBadge(key, moreFiltersTouched);
                    case 'Budget':
                        return !isSmallScreen && budget && renderBadge(key, budgetCount);
                    case 'Guests':
                        return !isSmallScreen && guests && renderBadge(key, guestCount);
                    case 'Services':
                        return !isSmallScreen && services && renderBadge(key, srCount);
                    case 'Alcohol & Beverages':
                        return !isSmallScreen && byo && renderBadge(key, byoCount);
                    default:
                        return;
                }
            }}
            isSticky={isSmallScreen}
            renderFilters={renderFilters}
            renderSearchFilter={renderSearchFilter}
        />
    );
};
